import type { ReactNode } from 'react';
import { shortPersonName } from '../../shared/person/person_name.js';
import { PersonAvatar, type PersonItem } from '../../shared/person/person_row.js';
import type { TravelerFieldSearch } from '../../shared/person/who_search.js';
import { TravelerSingleField, type TravelerSingleCopy } from '../../shared/person/who_single.js';

/** A lista do QUEM: a mesma cópia do seletor de pessoas dos outros campos,
 *  menos o rótulo (é o `driverLabel` da barra). */
export type RentalDriverCopy = TravelerSingleCopy;

/** Só o nome, sem escolha: o host que ainda não reserva para outra pessoa (o
 *  card da conversa) mostra quem vai dirigir com o mesmo retrato do seletor. */
function DriverReadOnly({
  driver,
  label,
}: {
  readonly driver: PersonItem;
  readonly label: string;
}): ReactNode {
  // `iuk-field-pill`: no border, but the same metrics as the pill beside it,
  // including the 44px touch variant (`FIELD_PILL_TOUCH_SCOPE_CLASS`).
  return (
    <p
      className="iuk-field-pill text-ui inline-flex h-9 min-w-0 items-center gap-1.5 px-3"
      title={driver.name}
    >
      <span className="sr-only">{label}: </span>
      {driver.name.trim() && (
        <PersonAvatar name={driver.name} photoUrl={driver.photoUrl} size="xs" />
      )}
      <span className="truncate">{shortPersonName(driver.name)}</span>
    </p>
  );
}

/**
 * O QUEM do carro: o QUEM de uma pessoa só (`TravelerSingleField`) — o carro tem
 * um condutor só. Sem `search`/`onChange` o campo é só leitura.
 */
export function RentalDriverField({
  driver,
  label,
  search,
  copy,
  disabled,
  locale,
  onChange,
}: {
  readonly driver: PersonItem;
  readonly label: string;
  readonly search?: TravelerFieldSearch;
  readonly copy?: RentalDriverCopy;
  readonly disabled: boolean;
  readonly locale?: string;
  readonly onChange?: (driver: PersonItem) => void;
}): ReactNode {
  if (!search || !copy || !onChange) return <DriverReadOnly driver={driver} label={label} />;
  return (
    <TravelerSingleField
      person={driver}
      label={label}
      search={search}
      copy={copy}
      disabled={disabled}
      {...(locale ? { locale } : {})}
      onChange={onChange}
    />
  );
}
